import { Grid, Link, ListItem, ListItemText, makeStyles, useMediaQuery, useTheme } from '@material-ui/core';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import T from 'src/components/content-manager/translator/T';
import Currency from 'src/components/elements/formats/Currency';
import HorizontalList from 'src/components/elements/HorizontalList';
import Section from 'src/components/elements/Section';
import CountryLangSelector from 'src/components/page/CountryLangSelector';
import { VolumeDiscountDiscover } from 'src/components/product-volume-discounts';
import { ROUTE_ACCOUNT_CREDITS } from 'src/config/account-routes';
import { ROUTE_REGISTER } from 'src/config/routes';
import { useAccount } from 'src/core/hooks/account';
import { SiteLayoutContext } from 'src/core/provider/SiteLayoutContext';
import { useSiteDefinition } from 'src/core/provider/SiteProvider';
import { fetchCustomerData } from 'src/store/customer/data/actions';
import { getCustomerData } from 'src/store/customer/data/selectors';
import ShippingCostTableDiscover from '../shop/ShippingCostTableDiscover';
import FreeShippingCostSnippet from './info/FreeShippingCostSnippet';
import VolumeDiscountSnippet from './info/VolumeDiscountSnippet';

const useStyles = makeStyles(theme => ({
    root: {
        fontSize: '0.9rem',
        lineHeight: '0.7rem',
    },
    grow: {
        flexGrow: 1,
    },
    hideMobile: {
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    list: {
        color: 'white',
    },
}));

interface TopBarProps {
    onSignOut?: () => void;
    loginLabel?: React.ReactNode;
    logoutLabel?: React.ReactNode;
    registerLabel?: React.ReactNode;
}

const TopBar: React.FC<TopBarProps> = ({ onSignOut, loginLabel, logoutLabel, registerLabel }) => {
    const classes = useStyles();
    const { showVolumeDiscount, showCredits } = React.useContext(SiteLayoutContext);
    const site = useSiteDefinition();
    const { isLoggedIn, signIn, signOut } = useAccount();
    const dispatch = useDispatch();
    const theme = useTheme();
    const isDownMd = useMediaQuery(theme.breakpoints.down('md'));

    // Fetch ProductGroups
    useEffect(() => {
        if (isLoggedIn) dispatch(fetchCustomerData());
    }, [dispatch, isLoggedIn]);

    const customerData = useSelector(getCustomerData);

    const handleSignInOut = () => {
        isLoggedIn ? (onSignOut ?? signOut)() : signIn();
    };

    return (
        <Section
            component="div"
            sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
            spacing="compact"
            className={classes.root}>
            <Grid container spacing={1} justifyContent="space-between" alignItems="center">
                {!isDownMd && (
                    <Grid item className={classes.grow}>
                        <HorizontalList dense className={classes.list}>
                            <ShippingCostTableDiscover
                                trigger={onClick => (
                                    <ListItem button onClick={onClick}>
                                        <FreeShippingCostSnippet />
                                    </ListItem>
                                )}
                            />
                            {showVolumeDiscount && (
                                <VolumeDiscountDiscover
                                    trigger={onClick => (
                                        <ListItem button onClick={onClick}>
                                            <VolumeDiscountSnippet />
                                        </ListItem>
                                    )}
                                />
                            )}
                        </HorizontalList>
                    </Grid>
                )}
                <Grid item>
                    <HorizontalList dense className={classes.list}>
                        {showCredits && isLoggedIn && customerData.content && (
                            <ListItem>
                                <ListItemText disableTypography>
                                    <Link {...ROUTE_ACCOUNT_CREDITS.componentProps} style={{ color: 'white' }}>
                                        <T k={ROUTE_ACCOUNT_CREDITS.t_long} />
                                        &nbsp;
                                        <Currency
                                            amount={customerData.content.availableCredit}
                                            currencyIso={customerData.content.currencyIso}
                                        />
                                    </Link>
                                </ListItemText>
                            </ListItem>
                        )}
                        {!isLoggedIn && site.registerEnabled !== false && (
                            <ListItem>
                                <ListItemText disableTypography>
                                    <Link {...ROUTE_REGISTER.componentProps} style={{ color: 'white' }}>
                                        {registerLabel ?? <T k={ROUTE_REGISTER.t_name} />}
                                    </Link>
                                </ListItemText>
                            </ListItem>
                        )}
                        <ListItem button onClick={handleSignInOut}>
                            <ListItemText disableTypography>
                                {isLoggedIn
                                    ? logoutLabel ?? <T k="routes:logout.name" />
                                    : loginLabel ?? <T k="routes:login.name" />}
                            </ListItemText>
                        </ListItem>
                    </HorizontalList>
                </Grid>
                <Grid item>
                    <CountryLangSelector hideIcon={isDownMd} showLabel={isDownMd ? 'language' : undefined} />
                </Grid>
            </Grid>
        </Section>
    );
};

export default TopBar;
